import { useRef, forwardRef, useImperativeHandle, ReactElement } from 'react'
import { Mesh, Vector3, PerspectiveCamera, OrthographicCamera } from 'three'
import { BasicMeshProps, useScaleObjectWithCamera } from '@modugen/ui-toolkit'
import { a, useSpring } from '@react-spring/three'
import { MeshStandardMaterialProps, useFrame } from '@react-three/fiber'

export interface ScalingIndicatorProps extends BasicMeshProps {
  size?: number
  color?: string
  opacity?: number
  /**
   * Only applies to sphere indicator
   */
  maxScale?: number
  materialProps?: MeshStandardMaterialProps
  animated?: boolean
}

const AnimatedScalingIndicator = forwardRef(function ScalingIndicator(
  {
    size = 0.03,
    color = 'red',
    opacity,
    materialProps,
    maxScale = 1.7,
    animated = true,
    ...meshProps
  }: ScalingIndicatorProps,
  ref,
): ReactElement {
  const meshRef = useRef<Mesh>(null)

  useScaleObjectWithCamera(meshRef)

  // expose internal ref to the outside
  useImperativeHandle(ref, () => meshRef.current)

  const { scale } = useSpring({
    to: async next => {
      while (animated) {
        await next({ scale: 1 })
        await next({ scale: maxScale })
      }
    },
    from: { scale: 1 },
    reset: true,
    config: { duration: 800 },
  })

  useFrame(({ camera }) => {
    if (!meshRef.current) return

    let scaleFactor
    if (camera instanceof OrthographicCamera) {
      scaleFactor = (camera.top - camera.bottom) / camera.zoom
    } else if (camera instanceof PerspectiveCamera) {
      const objectWorldPosition = new Vector3()
      meshRef.current.getWorldPosition(objectWorldPosition)

      const cameraWorldPosition = new Vector3()
      camera.getWorldPosition(cameraWorldPosition)

      scaleFactor =
        objectWorldPosition.distanceTo(cameraWorldPosition) *
        ((1.9 * Math.tan((Math.PI * camera.fov) / 360)) / camera.zoom)
    }

    if (scaleFactor && scale.get()) {
      meshRef.current.scale.set(1, 1, 1).multiplyScalar((scaleFactor / 4) * scale.get())
    }
  })

  return (
    <a.mesh ref={meshRef} {...meshProps} scale={scale}>
      <sphereGeometry args={[size]} />
      <meshStandardMaterial
        color={color}
        transparent={opacity !== undefined}
        opacity={opacity !== undefined ? opacity : 1}
        {...materialProps}
      />
    </a.mesh>
  )
})

export default AnimatedScalingIndicator
